import Cookies from "js-cookie";

export const ACCESS_COOKIE = "access_token";
export const REFRESH_COOKIE = "refresh_token";

// O Passport rotaciona o refresh_token a cada uso e revoga o anterior. Se duas
// abas tomam 401 juntas e mandam o mesmo refresh, a segunda recebe 401 e, sem
// coordenação, apagava o refresh novo que a primeira tinha acabado de gravar —
// derrubando a sessão das duas. A trava do navegador (Web Locks) faz as abas
// renovarem uma de cada vez.
const LOCK_NAME = "voomp-refresh-token";

function withTabLock(fn) {
  const locks = typeof navigator !== "undefined" && navigator.locks;
  if (!locks || !locks.request) return fn();

  // Se a própria trava falhar (documento inativo, origem opaca), renova sem ela
  // em vez de derrubar a sessão. Erro vindo de dentro de fn não é repetido.
  let started = false;
  return locks
    .request(LOCK_NAME, function () {
      started = true;
      return fn();
    })
    .catch(function (error) {
      if (started) throw error;
      return fn();
    });
}

// O Passport recusou o refresh (expirado, revogado, client inválido). Rede,
// timeout (inclusive 408 do balanceador), 5xx e 429 são passageiros: a sessão
// continua e a próxima request tenta de novo.
function isRejectedByServer(error) {
  const status = error && error.response && error.response.status;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

function noop() {}

/**
 * Cria a função de renovação do access token.
 *
 * `refresh(usedToken)` resolve com o "Bearer ..." para repetir a request que
 * tomou 401 com `usedToken`, ou com null quando a sessão acabou. Rejeita quando
 * a renovação falhou por motivo passageiro — nesse caso o refresh é mantido.
 *
 * `adoptToken` é chamado quando o token vem do cookie, renovado por outra aba:
 * o estado desta aba (store, header padrão) ainda aponta para o antigo.
 */
export function createRefresher({
  requestToken,
  persistTokens,
  adoptToken = noop,
  cookies = Cookies,
  lock = withTabLock,
}) {
  // Uma renovação por vez dentro da aba: requests que tomam 401 juntas esperam
  // a mesma promise.
  let inFlight = null;

  function adoptCurrent() {
    const current = cookies.get(ACCESS_COOKIE) || null;
    if (current) adoptToken(current);
    return current;
  }

  function renew(usedToken) {
    // Já dentro da trava: se o cookie não é mais o token que tomou 401, outra
    // aba (ou outra request desta) renovou enquanto esta esperava.
    const current = cookies.get(ACCESS_COOKIE);
    if (current && current !== usedToken) return adoptCurrent();

    const refreshToken = cookies.get(REFRESH_COOKIE);
    if (!refreshToken) return null;

    return requestToken(refreshToken)
      .then(persistTokens)
      .catch(function (error) {
        if (!isRejectedByServer(error)) throw error;

        // Navegador sem Web Locks: se o cookie mudou, outra aba renovou no meio
        // do caminho e a sessão continua válida — não apagar o refresh dela.
        if (cookies.get(REFRESH_COOKIE) !== refreshToken) return adoptCurrent();

        // Refresh expirado ou revogado: some com ele para não ficar tentando.
        cookies.remove(REFRESH_COOKIE);
        return null;
      });
  }

  return function refresh(usedToken) {
    if (inFlight) {
      // Renovação aberta por outra request, com outro token. Se ela só adotou
      // o token que esta acabou de usar e tomou 401, esta precisa da renovação
      // de verdade — senão desistiria e deslogaria com um refresh válido.
      return inFlight.then(function (token) {
        return token && token === usedToken ? refresh(usedToken) : token;
      });
    }

    inFlight = Promise.resolve()
      .then(function () {
        return lock(function () {
          return renew(usedToken);
        });
      })
      .finally(function () {
        inFlight = null;
      });

    return inFlight;
  };
}
